import { ArrowRight } from "@phosphor-icons/react"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { type ReactNode, useState } from "react"
import Container from "../common/Container"
import MarketingHeading from "../common/MarketingHeading"
import DataWarehouseIllustration from "./DataWarehouseIllustration"
import ELTPipelineIllustration from "./ELTPipelineIllustration"
import OnchainFundamentalsIllustration from "./OnchainFundamentalsIllustration"

interface FeatureCardProps {
  title: string
  description: string
  illustration: ReactNode
  dimmed: boolean
  onMouseEnter: () => void
}

const FeatureCard = ({
  title,
  description,
  illustration,
  dimmed,
  onMouseEnter,
}: FeatureCardProps) => {
  return (
    <div
      className="flex flex-col gap-4 transition-opacity duration-300"
      style={{ opacity: dimmed ? 0.35 : 1 }}
      onMouseEnter={onMouseEnter}
    >
      <div className="w-full rounded-xl border border-border-default bg-bg-surface/30 overflow-hidden flex items-center justify-center">
        {illustration}
      </div>
      <div className="flex flex-col gap-2">
        <h3 className="text-lg md:text-xl font-medium tracking-tight text-fg-default">{title}</h3>
        <p className="text-sm text-fg-secondary leading-relaxed">{description}</p>
      </div>
    </div>
  )
}

const FullStackDataPlatform = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  const features = [
    {
      title: "Source of truth for onchain data",
      description:
        "Every onchain metric traces back to the individual block and transaction. Raw blockchain data is ingested directly from RPC nodes across 100+ blockchains, decoded at the smart contract level, and stored in a proprietary petabyte-scale data warehouse.",
      illustration: <DataWarehouseIllustration paused={hoveredIndex !== null} />,
    },
    {
      title: "Standardized and comparable by design",
      description:
        "Every cross-protocol comparison is defensible. Project-specific business logic is mapped to standard financial and usage metrics, applied consistently across 1,200+ applications and 7,000+ tokenized assets.",
      illustration: <ELTPipelineIllustration paused={hoveredIndex !== null} />,
    },
    {
      title: "Data you can act on",
      description:
        "Token Terminal covers the full onchain market across blockchains, DeFi protocols, and tokenized assets. Explore metrics in Explorer, build custom analysis in Studio, or query our data directly via API or MCP. Our data is standardized, transparent, and ready for due diligence.",
      illustration: <OnchainFundamentalsIllustration paused={hoveredIndex !== null} />,
    },
  ]

  return (
    <section className="py-16 md:py-24 lg:py-32">
      <Container>
        <div className="flex flex-col gap-12 md:gap-16">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex flex-col gap-4 max-w-3xl">
              <MarketingHeading level={2} className="text-3xl md:text-4xl lg:text-5xl">
                A full stack onchain data platform
              </MarketingHeading>
              <p className="text-fg-secondary text-base text-balance leading-relaxed">
                Token Terminal provides the complete infrastructure for blockchain analytics. From
                raw blockchain data ingestion to standardized, comparable metrics—we handle the
                entire data pipeline so you can focus on insights, not infrastructure.
              </p>
            </div>
            <LinkButton
              variant="ghost"
              size="large"
              href="/resources/engineering"
              className="shrink-0 hidden md:inline-flex text-fg-secondary"
            >
              Read our engineering blog
              <Icon as={ArrowRight} />
            </LinkButton>
          </div>

          {/* Three Feature Cards */}
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-8"
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {features.map((feature, index) => (
              <FeatureCard
                key={feature.title}
                title={feature.title}
                description={feature.description}
                illustration={feature.illustration}
                dimmed={hoveredIndex !== null && hoveredIndex !== index}
                onMouseEnter={() => setHoveredIndex(index)}
              />
            ))}
          </div>
        </div>
      </Container>
    </section>
  )
}

export default FullStackDataPlatform
